// ============================================================
// NM Social — AI Compositor
// Logica de generare conținut cu Claude API
// ============================================================

import { t } from './i18n';

const CLAUDE_MODEL = 'claude-haiku-4-5';
const GENERARE_OPRITA = true;
// ── Sistem prompt general ────────────────────────────────────
const SYSTEM_PROMPT_GENERAL = `Ești un expert în comunicare politică și social media management pentru politicieni români.
Generezi conținut autentic, uman, aproape de cetățeni — nu discursuri goale, nu clișee politice.

REGULI ABSOLUTE:
- Scrii EXCLUSIV în limba română corectă
- Folosești DOAR cuvinte reale din limba română; NU inventa cuvinte și NU crea anglicisme (ex.: „lazitate" pornit din „laziness" e GREȘIT — corect: „lene", „trândăvie", „delăsare")
- Ton cald, direct, uman — niciodată corporatist sau bombastic
- Fraze scurte, verb activ
- INTERZIS: „thus", „furthermore", „hence", „în concluzie", „cu siguranță"
- INTERZIS: promisiuni vagi fără acțiuni concrete
- Fiecare text trebuie să sune ca și cum politicianul l-a scris personal
- Nu folosi template-uri generice — fiecare politician are vocea lui unică
- Răspunzi STRICT în formatul cerut, fără introduceri sau comentarii suplimentare`;

// ── Prompturi per rețea ──────────────────────────────────────
const PROMPTURI_RETEA = {
  facebook: (politician, tema, context, profil) => `
${SYSTEM_PROMPT_GENERAL}

PROFILUL CLIENTULUI (respectă vocea și tonul):
${profil || 'Politician român, ton cald și apropiat de cetățeni.'}

---

Creează o postare pentru Facebook pentru ${politician} despre: "${tema}".

${context ? `CONTEXT EVENIMENT:\n${context}\n` : ''}

REGULI FACEBOOK (profilul are prioritate — dacă o regulă de aici intră în conflict cu profilul, profilul câștigă):
- Lungimea și tonul le dă PROFILUL clientului. Dacă profilul cere mesaje scurte, scrie scurt. Dacă profilul nu spune nimic despre lungime, ține postarea concisă (sub ~150 de cuvinte).
- Emoji: folosește-le DOAR dacă profilul le permite și doar în numărul permis de profil; altfel niciunul.
- Hashtag-uri: adaugă-le DOAR dacă profilul le cere; altfel nu pune deloc.
- Nu impune un tipar de discurs politic (hook forțat, logica „soluția politicianului", CTA standard de tip „Scrie-mi în comentarii") dacă nu se potrivește cu profilul. Adaptează structura la stilul real al clientului.
- Fraze clare, fără jargon corporatist.

IMPORTANT: Textul trebuie să sune exact ca ${politician}, conform profilului — nu generic și fără personalitate.

Răspunde DOAR cu textul postării (plus hashtag-uri pe o linie separată, dacă profilul le cere). Fără introduceri sau explicații.`,

  instagram: (politician, tema, context, profil) => `
${SYSTEM_PROMPT_GENERAL}

PROFILUL CLIENTULUI (respectă vocea și tonul):
${profil || 'Politician român, ton cald și apropiat de cetățeni.'}

---

Creează conținut pentru Instagram Feed pentru politicianul ${politician} despre: "${tema}".

${context ? `CONTEXT EVENIMENT:\n${context}\n` : ''}

REGULI INSTAGRAM:
- Max 2.200 caractere (aprox. 300-350 cuvinte)
- Primele 125 caractere trebuie să fie extrem de captivante
- Între 8 și 10 emoji relevante integrate în text
- Propoziții scurte, stil conversațional
- Încheie cu o întrebare care invită la comentarii

Propune și 5 titluri scurte (max 4-5 cuvinte) pentru un carousel:
1. Problema principală
2. Cauza sau contextul
3. Efectele negative
4. Soluția politicianului
5. Apel la acțiune

IMPORTANT: Textul trebuie să sune exact ca ${politician}, nu ca un politician generic.

Răspunde STRICT în formatul următor, fără introduceri:
1. TEXT POSTARE:
[text complet în paragrafe scurte]

2. CAROUSEL TITLES:
- Slide 1: ...
- Slide 2: ...
- Slide 3: ...
- Slide 4: ...
- Slide 5: ...`,

  tiktok: (politician, tema, context, profil) => `
${SYSTEM_PROMPT_GENERAL}

PROFILUL CLIENTULUI (respectă vocea și tonul):
${profil || 'Politician român, ton cald și apropiat de cetățeni.'}

---

Creează un script de monolog pentru video vertical (9:16), între 30 și 50 secunde, pentru TikTok.
Politicianul: ${politician}. Tema: "${tema}".

${context ? `CONTEXT:\n${context}\n` : ''}

REGULI TIKTOK:
- Doar monolog, propoziții scurte, clare, conversaționale
- Structură:
  • Începe cu o întrebare directă sau „Știai că…?"
  • Prezintă problema, cauza și consecințele (rapid!)
  • Oferă soluția, beneficiile și rezultatul
  • Încheie cu: „Spune-mi în comentarii ce crezi!"
- Limbaj natural, ca și cum vorbești cu un prieten
- 30-50 secunde înseamnă aprox. 75-130 cuvinte de vorbit

IMPORTANT: Să sune ca ${politician}, nu ca un politician generic.

Răspunde STRICT în formatul următor, fără introduceri:
1. SCRIPT VIDEO:
[monolog]

2. OVERLAY TEXT:
[3-5 cuvinte cheie pentru text pe ecran]

3. CAPTION:
[caption scurt cu 3-5 hashtag-uri]`,

  youtube: (politician, tema, context, profil) => `
${SYSTEM_PROMPT_GENERAL}

PROFILUL CLIENTULUI (respectă vocea și tonul):
${profil || 'Politician român, ton cald și apropiat de cetățeni.'}

---

Creează un script pentru YouTube Short (video vertical, 30-60 secunde) pentru politicianul ${politician} despre: "${tema}".

${context ? `CONTEXT:\n${context}\n` : ''}

REGULI YOUTUBE SHORTS:
- Script monolog, propoziții clare și captivante
- Structură: Hook puternic → Problemă → Soluție → CTA
- 60-150 cuvinte (30-60 secunde de vorbit)
- Primul cuvânt/frază trebuie să oprească scroll-ul
- Încheie cu „Abonează-te pentru mai mult" sau întrebare

IMPORTANT: Să sune ca ${politician}, nu generic.

Răspunde STRICT în formatul următor:
1. SCRIPT:
[monolog complet]

2. TITLU VIDEO:
[titlu atractiv pentru YouTube, max 60 caractere]

3. DESCRIERE:
[descriere scurtă, 2-3 propoziții + hashtag-uri]`,

  twitter: (politician, tema, context, profil) => `
${SYSTEM_PROMPT_GENERAL}

PROFILUL CLIENTULUI (respectă vocea și tonul):
${profil || 'Politician român, ton cald și direct.'}

---

Creează un thread pentru X (Twitter) din 7 tweet-uri despre "${tema}", pentru politicianul ${politician}.

${context ? `CONTEXT:\n${context}\n` : ''}

REGULI X/TWITTER:
- Fiecare tweet: MAX 280 caractere
- Tweet 1 = hook puternic, direct, conversațional
- Tweet-urile 2-6 = dezvoltă problema și soluția cu exemple scurte
- Tweet 7 = concluzie + întrebare pentru engagement
- Limbaj direct, fără artificii

IMPORTANT: Să sune ca ${politician}, nu generic.

Răspunde STRICT în formatul următor, fără introduceri:
1. TWEETS:
- Tweet 1: ...
- Tweet 2: ...
- Tweet 3: ...
- Tweet 4: ...
- Tweet 5: ...
- Tweet 6: ...
- Tweet 7: ...`,

  linkedin: (politician, tema, context, profil) => `
${SYSTEM_PROMPT_GENERAL}

PROFILUL CLIENTULUI (respectă vocea și tonul):
${profil || 'Politician român, ton profesional dar autentic.'}

---

Creează un text pentru LinkedIn despre "${tema}", pentru politicianul ${politician}.

${context ? `CONTEXT:\n${context}\n` : ''}

REGULI LINKEDIN:
- Lungime: 300-400 cuvinte, paragrafe scurte
- Hook puternic în prima propoziție (întrebare sau afirmație)
- Prezintă problema cu cauze și efecte clare
- O poveste scurtă sau exemplu real pentru autenticitate
- Soluția propusă de politician — de ce e realistă și necesară
- Beneficii concrete pentru societate și mediul economic
- Încheie cu o întrebare pentru rețeaua LinkedIn

Checklist:
✔ Hook conversațional
✔ Problemă, cauză, efecte
✔ Exemplu/poveste autentică
✔ Soluție clară + beneficii
✔ Întrebare finală pentru engagement
✔ Ton ferm, responsabil, cu emoție autentică

IMPORTANT: Să sune ca ${politician}, nu generic. Fără „thus", „furthermore", „hence".

Răspunde cu text simplu în paragrafe, fără introduceri automate.`,
};

// ── Funcție principală de generare ───────────────────────────
export async function genereazaContiut({ politician, tema, context, profilText, reteleSelectate, formate }) {
  if (!politician || !tema || reteleSelectate.length === 0) {
    throw new Error(t('compositor', 'lipsescDate'));
  }

  // Generăm pentru toate rețelele selectate în paralel
  const promisiuni = reteleSelectate.map(async (retea) => {
    const promptFn = PROMPTURI_RETEA[retea];
    if (!promptFn) return { retea, rezultat: null, eroare: t('compositor', 'reteaNecunoscuta') };

    // 16 septembrie 2026 (Faza 7): compozitorul vechi nu mai genereaza. Poarta
    // /api/generate a fost stearsa; generarea traieste in compozitorul nou
    // (functia genereaza-texte). Codul de mai jos ramane pana la stergerea
    // compozitorului vechi.
    if (GENERARE_OPRITA) return { retea, rezultat: null, eroare: t('compositor', 'generareMutata') };

    const prompt = promptFn(politician, tema, context, profilText);

    try {
      const response = await fetch('/api/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: CLAUDE_MODEL,
          max_tokens: 1500,
          messages: [{ role: 'user', content: prompt }],
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error?.message || t('compositor', 'eroareApiGen'));
      }

      const data = await response.json();
      const text = data.content?.[0]?.text || '';
      return { retea, rezultat: text, eroare: null };
    } catch (err) {
      return { retea, rezultat: null, eroare: err.message };
    }
  });

  const rezultate = await Promise.all(promisiuni);

  // Transformăm array în obiect { facebook: '...', instagram: '...', ... }
  return rezultate.reduce((acc, { retea, rezultat, eroare }) => {
    acc[retea] = { text: rezultat, eroare };
    return acc;
  }, {});
}

// ── Extras text din fișier Word (.docx) ─────────────────────
export async function extrageTextDinWord(fisier) {
  try {
    const mammoth = await import('mammoth');
    const arrayBuffer = await fisier.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    // Limităm la ~2000 tokens (aprox. 8000 caractere) pentru costuri
    return result.value.slice(0, 8000).trim();
  } catch (err) {
    console.error('Eroare extragere Word:', err);
    return null;
  }
}

// ── Parsează rezultatul per format de rețea ──────────────────
export function parseazaRezultat(retea, text) {
  if (!text) return { tip: 'simplu', continut: '' };

  switch (retea) {
    case 'instagram': {
      const parts = text.split(/\d\.\s+(?:TEXT POSTARE|CAROUSEL TITLES):/i);
      return {
        tip: 'instagram',
        textPostare: parts[1]?.trim() || text,
        carouselTitles: parts[2]?.trim() || '',
      };
    }
    case 'tiktok':
    case 'youtube': {
      const script = text.match(/1\.\s+SCRIPT(?:\sVIDEO)?:\n([\s\S]*?)(?=\n2\.)/i)?.[1]?.trim();
      const overlay = text.match(/2\.\s+(?:OVERLAY TEXT|TITLU VIDEO):\n([\s\S]*?)(?=\n3\.)/i)?.[1]?.trim();
      const caption = text.match(/3\.\s+(?:CAPTION|DESCRIERE):\n([\s\S]*?)$/i)?.[1]?.trim();
      return { tip: 'video', script: script || text, overlay, caption };
    }
    case 'twitter': {
      const tweets = text.match(/- Tweet \d+: .+/g) || [];
      return { tip: 'thread', tweets: tweets.map(t => t.replace(/^- Tweet \d+: /, '')) };
    }
    default: {
      const linii = text.split('\n');
      const liniHashtag = linii.filter(l => l.trim().startsWith('#')).join(' ');
      const textCurat = linii.filter(l => !l.trim().startsWith('#')).join('\n').trim();
      return { tip: 'simplu', continut: textCurat, hashtags: liniHashtag };
    }
  }
}